import { z } from 'zod';

import { SOMETHING_ELSE_LABEL } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/elicitation';

/** What the user gave an ask tool, read back from that tool's persisted result. */
export type WorkspaceAgentAskAnswer =
  | { kind: 'selected'; labels: string[]; customText?: string }
  | { kind: 'text'; text: string }
  | { kind: 'fields'; fields: { label: string; value: string }[] }
  | { kind: 'number'; value: number }
  | { kind: 'dismissed' }
  | { kind: 'unanswered'; reason: string };

const NonEmptyStringSchema = z.string().trim().min(1);

/** Every dock except yes/no can be closed without an answer; the tool then reports `dismissed`. */
const DismissedOutputSchema = z.object({ dismissed: z.literal(true) });

/**
 * One schema per ask tool, mirroring what that tool's handler writes to `output` in
 * `packages/agents/src/refactor/agents/workspace/tools/ask/`. The tool name picks the schema, so a
 * result is only ever checked against the shape its own tool can produce.
 */
const ASK_OUTPUT_SCHEMAS = {
  ask_binary: z.object({ selectedLabel: NonEmptyStringSchema }),
  ask_single: z.union([
    z.object({ selectedLabels: z.array(z.string()), customText: NonEmptyStringSchema.optional() }),
    DismissedOutputSchema,
  ]),
  ask_multi: z.union([
    z.object({ selectedLabels: z.array(z.string()), customText: NonEmptyStringSchema.optional() }),
    DismissedOutputSchema,
  ]),
  ask_search: z.union([z.object({ selectedLabels: z.array(z.string()) }), DismissedOutputSchema]),
  ask_describe: z.union([z.object({ text: NonEmptyStringSchema }), DismissedOutputSchema]),
  ask_fields: z.union([
    z.object({ fieldValues: z.array(z.object({ label: NonEmptyStringSchema, value: z.string() })) }),
    DismissedOutputSchema,
  ]),
  ask_stepper: z.union([z.object({ value: z.number().int() }), DismissedOutputSchema]),
} as const;

export type WorkspaceAgentAskToolName = keyof typeof ASK_OUTPUT_SCHEMAS;

const ASK_TOOL_NAMES = new Set<string>(Object.keys(ASK_OUTPUT_SCHEMAS));

export const isWorkspaceAgentAskToolName = (name: string): name is WorkspaceAgentAskToolName =>
  ASK_TOOL_NAMES.has(name);

const AskToolResultContentSchema = z.object({
  success: z.boolean(),
  output: z.unknown(),
  error: z.string().nullable().optional(),
});

const UNANSWERED_FALLBACK_REASON = 'No answer';

type AskOutput = {
  [K in WorkspaceAgentAskToolName]: z.infer<(typeof ASK_OUTPUT_SCHEMAS)[K]>;
}[WorkspaceAgentAskToolName];

const toAnswer = (output: AskOutput): WorkspaceAgentAskAnswer => {
  if ('dismissed' in output) {
    return { kind: 'dismissed' };
  }

  if ('selectedLabel' in output) {
    return { kind: 'selected', labels: [output.selectedLabel] };
  }

  if ('selectedLabels' in output) {
    const customText = 'customText' in output ? output.customText : undefined;
    // "Something else" is the free-text escape hatch, so the typed answer stands in for its label.
    const labels =
      customText === undefined
        ? output.selectedLabels
        : output.selectedLabels.filter((label) => label !== SOMETHING_ELSE_LABEL);

    return customText === undefined ? { kind: 'selected', labels } : { kind: 'selected', labels, customText };
  }

  if ('text' in output) {
    return { kind: 'text', text: output.text };
  }

  if ('fieldValues' in output) {
    return { kind: 'fields', fields: output.fieldValues };
  }

  return { kind: 'number', value: output.value };
};

export type ParseWorkspaceAgentAskAnswerParams = {
  /** The persisted `toolResult` row's content. */
  content: string | null;
  /** The ask tool that produced it, from the result's metadata or the matching tool call. */
  toolName: string;
};

/**
 * Reads the user's answer out of an ask tool's result content. The dock closes once answered, so
 * this is the only record of the choice the thread can show. Returns null when the tool is not an
 * ask tool or the content is not the shape that tool writes.
 */
export const parseWorkspaceAgentAskAnswer = ({
  content,
  toolName,
}: ParseWorkspaceAgentAskAnswerParams): WorkspaceAgentAskAnswer | null => {
  if (!content || !isWorkspaceAgentAskToolName(toolName)) {
    return null;
  }

  let json: unknown;

  try {
    json = JSON.parse(content);
  } catch {
    return null;
  }

  const result = AskToolResultContentSchema.safeParse(json);

  if (!result.success) {
    return null;
  }

  if (!result.data.success) {
    return { kind: 'unanswered', reason: result.data.error?.trim() || UNANSWERED_FALLBACK_REASON };
  }

  const output = ASK_OUTPUT_SCHEMAS[toolName].safeParse(result.data.output);

  return output.success ? toAnswer(output.data) : null;
};
